import React, { useState, useEffect, useRef } from 'react';
import { getSmsConversationMessages, sendSmsFromConversation } from '../../services/extension.service';
import { Loading } from '../common/Loading';

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString();
};

const Bubble = ({ message }) => {
  const isOutbound = message.direction === 'outbound';

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}>
      <div
        className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm leading-relaxed shadow-sm ${
          isOutbound
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{message.body}</p>
        <div className={`flex items-center gap-1 mt-1 text-xs ${isOutbound ? 'text-blue-200' : 'text-gray-400'} justify-end`}>
          <span>{formatTime(message.created_at)}</span>
          {isOutbound && message.status && (
            <span>
              {message.status === 'delivered' ? '✓✓' : message.status === 'sent' ? '✓' : '○'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const ConversationThread = ({ conversationId, phoneNumber, customerName, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const bottomRef = useRef(null);

  const loadMessages = async () => {
    // No conversation yet — skip loading
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    try {
      const res = await getSmsConversationMessages(conversationId, { limit: 100 });
      setMessages(res.data?.data || []);
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const res = await sendSmsFromConversation(conversationId, {
        to: phoneNumber,
        body: replyText.trim()
      });

      if (res.data?.success) {
        setReplyText('');
        setSuccess('Message sent!');
        setTimeout(() => setSuccess(''), 2000);
        await loadMessages();
      } else {
        setError(res.data?.error || 'Failed to send');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Group messages by date
  const groupedMessages = [];
  let lastDate = null;
  for (const msg of messages) {
    const date = formatDate(msg.created_at);
    if (date !== lastDate) {
      groupedMessages.push({ type: 'date', label: date, id: `date-${msg.id}` });
      lastDate = date;
    }
    groupedMessages.push({ type: 'message', data: msg });
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
        <div>
          <p className="font-semibold text-gray-900">{customerName || phoneNumber}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
          >
            Close
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50">
        {loading ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <p className="text-sm">No messages yet.</p>
            <p className="text-xs mt-1">Send a message to start the conversation.</p>
          </div>
        ) : (
          groupedMessages.map((item) => {
            if (item.type === 'date') {
              return (
                <div key={item.id} className="flex justify-center my-3">
                  <span className="text-xs text-gray-400 bg-gray-100 px-3 py-0.5 rounded-full">
                    {item.label}
                  </span>
                </div>
              );
            }
            return <Bubble key={item.data.id} message={item.data} />;
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      <form onSubmit={handleSend} className="px-4 py-3 border-t border-gray-200 bg-white shrink-0">
        {error && (
          <div className="mb-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
            {success}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !replyText.trim()}
            className="px-5 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConversationThread;
